/* R796 Battlegrounds page: the game shell (Obsidian Quiet round 2), in the R800 Battlegrounds 2
   menus (docs/BATTLEGROUNDS-2-DESIGN.md §10): the Hub's Home and its four tabs.
   Phone: the top bar (your Champion, tickets, gold, crystals and Settings), the view, and the
   tab bar Home | Hero | Party | Shop (the packs live in the Shop, the chapters on Home). Desktop (1200 px and up, Jan: its own
   layout): a header with the brand, your Champion, the wallet, Settings and Back to the Hall, the tabs as
   a left nav with Settings at its foot, and the view beside it.
   2026-09-28 (Jan: "the top panel can also be designed more dynamically and cleanly ... tickets refill at midnight
   pacific is way too long just show a timer"): the refill is a live timer on the ticket pill while tickets are below
   the day's count (its full time in the tooltip and label), the three pills share one wallet capsule, and a pill
   counts to its new value with a short glint whenever it changes. The chrome's own styles are #bg-shell-css.
   2026-10-05 (the menus redesign, docs/battlegrounds/menus-2026-10-05): the tab bar reads Home | Champion | Party | Shop,
   a book beside Settings opens the Codex (every effect and term), and Settings gains Endless speed and the Codex row
   in How to play's place.
   The view manager keeps one <section data-bg-panel> per screen. A route
   change crossfades the old screen out and the new one in (Jan's motion rule:
   nothing changes abruptly), scrolls to the top without smoothing, and keeps
   each screen's heading element stable so the router can focus it. Screens
   live in their own modules and are imported on first use. */

import { h, icon, portrait, pill, coin, crystal, ticket, logoMark, fmt, animateIn, animateOut, firstName, reduced, sheet, motionPref, setMotionPref, star, untilWords } from 'bg-ui';
import { TABS } from 'bg-router';
import * as homeView from 'bg-home';

const VIEW_MODULE = Object.freeze({ home: 'bg-hub', hero: 'bg-champion', party: 'bg-heroes', shop: 'bg-shop',
  level: 'bg-home', card: 'bg-hero', admin: 'bg-admin' });
const VIEW_TITLE = Object.freeze({ home: 'Home', hero: 'Champion', party: 'Party', shop: 'Shop', level: 'Level', card: 'Hero',
  admin: 'Game rarity' });
const TAB_ICON = Object.freeze({ home: 'home', hero: 'crown', party: 'heroes', shop: 'shop' });
/* home.dots (the server's red dots) on their tabs: something to claim or play on Home, a merge that is
   possible on Party (dots.heroes: spare copies, the same count as Party's Merge button), the daily gift in the Shop.
   The free Common Pack (home.free_pack.available) lights the Shop dot too, whatever the server's dots.shop says. */
const DOT_OF = Object.freeze({ home: ['battle', 'events'], party: ['heroes'], shop: ['shop'] });

/* when today's free tickets come back: the server's tickets.next_free_at while it is still ahead, else the next
   midnight in America/Los_Angeles, found with Intl so a daylight saving change never moves it (the Shop reads it too) */
export function nextPacificMidnight(now) {
  const n = now == null ? Date.now() : now;
  try {
    const p = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Los_Angeles', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date(n)).split('-').map(Number);
    const hourAt = ms => Number(new Intl.DateTimeFormat('en-US', { timeZone: 'America/Los_Angeles', hour: '2-digit', hourCycle: 'h23' }).format(new Date(ms)));
    for (const off of [7, 8]) { const t = Date.UTC(p[0], p[1] - 1, p[2] + 1, off); if (hourAt(t) === 0) return t; }
    return Date.UTC(p[0], p[1] - 1, p[2] + 1, 8);
  } catch (_) { return NaN; }
}
export function ticketRefillAt(t) {
  const s = t && t.next_free_at ? new Date(t.next_free_at).getTime() : NaN;
  return isFinite(s) && s > Date.now() ? s : nextPacificMidnight();
}
/* a time of day in the player's own clock, for a tooltip ("3:00 AM") */
export function clockWords(ms) {
  try { return new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' }).format(new Date(ms)); } catch (_) { return ''; }
}

export function createShell(ctx) {
  const store = ctx.store;
  let router = null, current = null, destroyed = false;
  const panels = {};

  /* ---------- top bar ---------- */
  const profImg = h('span', { class: 'bg-prof__img' });
  const profK = h('span', { class: 'bg-prof__k' });
  const profV = h('span', { class: 'bg-prof__v bg-num' });
  const prof = h('button', { type: 'button', class: 'bg-prof', 'data-bg-action': 'profile', on: { click: () => ctx.tab('hero') } },
    profImg, h('span', { class: 'bg-prof__txt' }, profK, profV));
  const pTickets = pill(ticket(16), h('span', { 'data-bg-v': 'tickets' }), 'Tickets', { 'data-bg-wallet': 'tickets' });
  const pGold = pill(coin(16), h('span', { 'data-bg-v': 'gold' }), 'Gold', { 'data-bg-wallet': 'gold' });
  const pCrystals = pill(crystal(16), h('span', { 'data-bg-v': 'crystals' }), 'Mythic crystals', { 'data-bg-wallet': 'crystals' });
  /* the refill timer rides beside the ticket pill, in one segment with it (a tag under it on a phone, inline on a
     desktop); it is the pill's sibling, so the pill's own text stays "Tickets 4/5", and it says its own words */
  const tTimerV = h('span', { class: 'bg-pill__tv', 'aria-hidden': 'true' });
  const tTimer = h('span', { class: 'bg-pill__timer', 'data-bg-ticket-timer': '', role: 'timer', hidden: true }, icon('history', 12, null, 2), tTimerV);
  const tSeg = h('span', { class: 'bg-tixseg' }, pTickets, tTimer);
  const hallBtn = h('a', { class: 'bg-btn bg-top__hall', href: ctx.hallUrl, 'data-bg-action': 'hall' }, icon('hall', 18), 'Back to the Hall');
  const gear = h('button', { type: 'button', class: 'bg-iconbtn bg-top__gear', 'data-bg-action': 'settings', 'aria-label': 'Settings', on: { click: () => settingsSheet(ctx) } }, icon('settings', 22));
  /* the Codex: every effect and term, one tap from any screen */
  const book = h('button', { type: 'button', class: 'bg-iconbtn bg-top__codex', 'data-bg-action': 'codex', 'data-fk': 'top-codex', 'aria-label': 'Codex, every effect and term',
    'aria-haspopup': 'dialog', on: { click: () => ctx.codex() } }, icon('codex', 22));
  const brand = h('a', { class: 'bg-brand', href: '#/home', 'aria-label': 'Battlegrounds home' }, h('span', { class: 'bg-brand__mark' }, logoMark(22)), h('span', { text: 'Battlegrounds' }));
  const top = h('header', { class: 'bg-top' }, brand, prof,
    h('div', { class: 'bg-pills' }, h('span', { class: 'bg-wallet' }, tSeg, pGold, pCrystals), book, gear, hallBtn));
  shellCss();
  /* the brand's diamond catches the light once as the page arrives */
  if (!reduced()) brand.classList.add('is-glint');
  const banner = h('p', { class: 'bg-banner', 'data-bg-banner': '', role: 'status', hidden: true, text: 'You are offline. Fights wait until you are back online.' });
  const adminBanner = h('p', { class: 'bg-banner bg-banner--admin', 'data-bg-admin-banner': '', hidden: true });

  /* ---------- tabs (+ the desktop side nav) ---------- */
  const tabButtons = TABS.map(t => h('button', { type: 'button', class: 'bg-tab', 'data-bg-tab': t.id,
    on: { click: () => { if (router) router.tab(t.id); } } },
    h('span', { class: 'bg-tab__ico' }, icon(TAB_ICON[t.id], 22, null, 1.5)),
    h('span', { class: 'bg-tab__label', text: t.label }),
    h('span', { class: 'bg-dot', 'data-bg-dot': t.id, hidden: true, 'aria-hidden': 'true' })));
  const dnav = h('div', { class: 'bg-dnav', role: 'group', 'aria-label': 'More' });
  const tabs = h('nav', { class: 'bg-tabs', 'aria-label': 'Battlegrounds' }, tabButtons, dnav);
  const views = h('div', { class: 'bg-views', id: 'bg-views' });
  const el = h('div', { class: 'bg-shell' }, top, banner, adminBanner, tabs, views);

  /* ---------- chrome from the store ---------- */
  function setV(node, label, text, extra) {
    const v = node.querySelector('[data-bg-v]');
    v.replaceChildren(h('span', { class: 'bg-vh', text: label + ' ' }), text, extra || '');
  }
  /* the numbers each pill shows now: a change after the first paint counts to the new value and glints (up or down) */
  const shown = {};
  function paintNum(key, node, label, value) {
    const prev = shown[key];
    shown[key] = value;
    if (counting[key]) return;
    if (prev != null && value != null && prev !== value && !reduced()) { countTo(key, prev, value, 560); glint(node, value > prev); return; }
    setV(node, label, fmt(value));
  }
  function paintChrome(s) {
    const hm = s.home || {}, w = hm.wallet || {}, t = hm.tickets || {};
    paintNum('gold', pGold, 'Gold', w.gold == null ? null : Number(w.gold) || 0);
    paintNum('crystals', pCrystals, 'Crystals', w.crystals == null ? null : Number(w.crystals) || 0);
    setV(pTickets, 'Tickets', fmt(t.free_left) + '/' + fmt(t.free_per_day), (t.banked | 0) > 0 ? h('span', { class: 'bg-pill__extra', text: ' +' + fmt(t.banked) }) : '');
    const tk = t.free_left == null ? null : (t.free_left | 0) + (t.banked | 0);
    if (shown.tickets != null && tk != null && tk !== shown.tickets) glint(pTickets, tk > shown.tickets);
    shown.tickets = tk;
    pGold.setAttribute('aria-label', 'Gold, ' + fmt(w.gold));
    pCrystals.setAttribute('aria-label', 'Mythic crystals, ' + fmt(w.crystals));
    paintTimer();
    /* your hero: the one in party slot 1, framed as the Hall frames it */
    const slot1 = Array.isArray(hm.slots) ? hm.slots.find(x => (x.slot | 0) === 1) : null;
    const card = slot1 && slot1.card_key ? ctx.cards.get(slot1.card_key) : null;
    if (card && card.character_id && ctx.wantFaces) ctx.wantFaces([card.character_id]);
    const frame = card && ctx.hallFace ? (ctx.hallFace(card.character_id) || {}).frame || null : null;
    const key = card ? card.card_key + '|' + card.img + '|' + card.rarity + '|' + (frame ? JSON.stringify(frame) : '') : 'none';
    if (profImg.dataset.k !== key) {
      profImg.dataset.k = key;
      profImg.replaceChildren(card ? portrait({ img: card.img, name: card.name, rarity: card.rarity, size: 36, radius: 8, alt: '', glow: false, frame: frame })
        : h('span', { class: 'bg-prof__ph', 'aria-hidden': 'true' }, icon('heroes', 20)));
    }
    const totalStars = hm.stars ? (hm.stars.total | 0) : 0;
    const starWords = totalStars === 1 ? '1 star' : fmt(totalStars) + ' stars';
    profK.textContent = card ? firstName(card.name) : 'Your hero';
    if (profV.dataset.w !== starWords) { profV.dataset.w = starWords; profV.replaceChildren(star(13, true), h('span', { text: starWords })); }
    prof.setAttribute('aria-label', (card ? card.name + ', ' : '') + starWords + '. Open the Champion tab.');
    /* red dots only while something is actionable (home.dots, and the free Common Pack while it waits) */
    /* before the first attempt nothing competes with the first fight (research playbook) */
    const first = homeView.isFirstVisit(ctx), route = s.route || {};
    /* R812: the Daily calendar lights the Shop too (its claim pays the day and the free pack) */
    const dots = first ? {} : Object.assign({}, hm.dots || {}, (hm.free_pack && hm.free_pack.available === true) || (hm.daily && hm.daily.available === true) ? { shop: true } : null);
    for (const b of tabButtons) {
      const id = b.getAttribute('data-bg-tab');
      if (route.tab === id) b.setAttribute('aria-current', 'page'); else b.removeAttribute('aria-current');
      const dot = b.querySelector('[data-bg-dot]');
      if (dot) dot.hidden = !((DOT_OF[id] || []).some(k => dots[k] === true));
    }
    banner.hidden = !!s.net.online;
    if (hm.admin === true && hm.open === false) {
      adminBanner.hidden = false;
      adminBanner.textContent = 'The world is closed to players. You can play because you are an admin.';
    } else adminBanner.hidden = true;
    /* a banner takes height from the home's one screen (BGLAYOUT): the layout makes room for it */
    el.classList.toggle('has-banner', !banner.hidden || !adminBanner.hidden);
    paintDnav();
  }
  /* the desktop nav's extras, under a rule: Settings (and the admin's page). How to play and History live inside
     Settings (Jan, 2026-09-28: "all of that can be inside of settings") */
  function paintDnav() {
    const sig = ctx.home().admin === true ? 'admin' : 'player';
    if (dnav.dataset.sig === sig) return;
    dnav.dataset.sig = sig;
    const item = (id, ico, label, act) => h('button', { type: 'button', class: 'bg-dnav__item', 'data-bg-side': id, on: { click: e => act(e.currentTarget) } }, icon(ico, 20), h('span', { text: label }));
    const nodes = [item('settings', 'settings', 'Settings', () => settingsSheet(ctx))];
    if (ctx.home().admin === true) nodes.push(item('admin', 'pencil', 'Game rarity', () => ctx.go('#/admin')));
    dnav.replaceChildren(...nodes);
  }
  /* a counter that ticks up to the reply's number while coins land (the daily gift); the store
     keeps the truth and repaints the pill the moment the count ends */
  const counting = {};
  function pillOf(key) { return key === 'gold' ? pGold : key === 'crystals' ? pCrystals : key === 'tickets' ? pTickets : null; }
  function countTo(key, from, to, ms) {
    const node = pillOf(key);
    if (!node || reduced() || !(ms > 0) || from === to || key === 'tickets') return Promise.resolve();
    const v = node.querySelector('[data-bg-v]'), label = key === 'gold' ? 'Gold' : 'Crystals';
    counting[key] = true;
    shown[key] = to;
    return new Promise(resolve => {
      const t0 = performance.now();
      const step = now => {
        const k = Math.min(1, (now - t0) / ms), e = 1 - Math.pow(1 - k, 3);
        v.replaceChildren(h('span', { class: 'bg-vh', text: label + ' ' }), fmt(Math.round(from + (to - from) * e)));
        if (k < 1) requestAnimationFrame(step);
        else {
          counting[key] = false;
          /* the store moved on while this counted: show where it is now */
          if (shown[key] != null && shown[key] !== to) setV(node, label, fmt(shown[key]));
          resolve();
        }
      };
      requestAnimationFrame(step);
    });
  }
  /* a pill whose value changed: a light sweeps across it and the number flashes (gold going up, quieter going down) */
  function glint(node, up) {
    if (!node || reduced()) return;
    node.classList.remove('is-glint', 'is-up', 'is-down'); void node.offsetWidth;
    node.classList.add('is-glint', up ? 'is-up' : 'is-down');
    clearTimeout(node.__glintT);
    node.__glintT = setTimeout(() => node.classList.remove('is-glint', 'is-up', 'is-down'), 900);
  }
  /* the ticket pill's refill timer: shown while tickets are below the day's count; the count (and a fresh home read)
     comes back when the timer runs out. The whole time is in the tooltip and the label. */
  let timerAt = 0;
  function paintTimer() {
    const t = (store.get().home || {}).tickets || {};
    const per = t.free_per_day | 0, free = t.free_left | 0, banked = t.banked | 0;
    const base = 'Tickets, ' + fmt(free) + ' of ' + fmt(per) + ' free left today' + (banked > 0 ? ', ' + fmt(banked) + ' saved' : '');
    if (timerAt && Date.now() >= timerAt) { timerAt = 0; ctx.refreshHome(); }
    const at = per > 0 && free < per ? ticketRefillAt(t) : NaN;
    pTickets.setAttribute('aria-label', base);
    if (!(at > Date.now())) {
      timerAt = 0; tTimer.hidden = true; tSeg.classList.remove('has-timer'); tSeg.removeAttribute('title');
      return;
    }
    timerAt = at;
    const w = untilWords(at - Date.now()), when = clockWords(at);
    tTimerV.textContent = w;
    tTimer.hidden = false; tSeg.classList.add('has-timer');
    tTimer.setAttribute('aria-label', 'Tickets refill to ' + fmt(per) + ' in ' + w);
    tSeg.title = 'Back to ' + fmt(per) + ' tickets' + (when ? ' at ' + when : '') + ', in ' + w;
  }
  const timerTick = setInterval(() => { if (!destroyed) paintTimer(); }, 15000);
  function bump(keys) {
    if (reduced()) return;
    for (const k of keys || []) {
      const node = k === 'gold' ? pGold : k === 'crystals' ? pCrystals : k === 'tickets' ? pTickets : null;
      if (!node) continue;
      node.classList.remove('bg-bump'); void node.offsetWidth; node.classList.add('bg-bump');
    }
  }

  /* ---------- views ---------- */
  function panelFor(view) {
    if (panels[view]) return panels[view];
    const heading = h('h1', { id: 'bg-h-' + view, class: 'bg-view__h', 'data-bg-heading': '', tabindex: '-1', text: VIEW_TITLE[view] || '' });
    const body = h('div', { class: 'bg-view__body' });
    const section = h('section', { class: 'bg-view bg-view--' + view, 'data-bg-panel': view, 'aria-labelledby': 'bg-h-' + view, hidden: true }, heading, body);
    views.appendChild(section);
    panels[view] = { view: view, section: section, heading: heading, body: body, mod: view === 'level' ? homeView : null, first: true, loading: null, error: null };
    return panels[view];
  }
  function focusKeyOf(node) { return node && node.closest && node.closest('[data-fk]') ? node.closest('[data-fk]').getAttribute('data-fk') : null; }
  function renderPanel(view) {
    const p = panels[view];
    if (!p || destroyed) return;
    const s = store.get();
    if (!p.mod) {
      if (!p.loading) {
        p.body.replaceChildren(h('div', { class: 'bg-empty', 'aria-busy': 'true' }, h('span', { class: 'bg-spin' })));
        p.loading = ctx.load(VIEW_MODULE[view], true).then(m => { p.mod = m; p.loading = null; p.error = null; renderPanel(view); }, e => {
          p.loading = null; p.error = e;
          p.body.replaceChildren(h('div', { class: 'bg-inline-err', role: 'alert' }, h('span', { text: 'This screen could not load. Check your connection and try again.' }),
            h('button', { type: 'button', class: 'bg-btn', text: 'Try again', on: { click: () => { p.error = null; renderPanel(view); } } })));
        });
      }
      return;
    }
    const fk = focusKeyOf(document.activeElement);
    const headingFocused = document.activeElement === p.heading;
    const hadFocusInside = p.section.contains(document.activeElement) && !headingFocused;
    let out = null;
    try { out = p.mod.render(ctx, s.route || {}, p); }
    catch (e) {
      try { console.error('[bg ' + view + ']', e); } catch (_) {}
      out = h('div', { class: 'bg-inline-err', role: 'alert' }, h('span', { text: 'This screen hit a problem. Try again.' }),
        h('button', { type: 'button', class: 'bg-btn', text: 'Try again', on: { click: () => renderPanel(view) } }));
    }
    if (out) {
      const nodes = Array.isArray(out) ? out : [out];
      /* a screen may place its own heading element anywhere in its tree */
      if (nodes.some(n => n === p.heading || (n && n.contains && n.contains(p.heading)))) p.body.replaceChildren(...nodes.filter(Boolean));
      else { p.body.replaceChildren(...nodes.filter(Boolean)); if (p.heading.parentNode !== p.section) p.section.insertBefore(p.heading, p.body); }
      if (p.heading.parentNode === p.section && p.section.firstChild !== p.heading) p.section.insertBefore(p.heading, p.body);
    }
    p.first = false;
    /* moving the heading into the screen's tree drops its focus: give it back (the router focused it) */
    if (headingFocused && document.activeElement !== p.heading) { try { p.heading.focus({ preventScroll: true }); } catch (_) {} }
    if (hadFocusInside && fk) {
      const again = p.section.querySelector('[data-fk="' + fk.replace(/"/g, '') + '"]');
      if (again) { try { again.focus({ preventScroll: true }); } catch (_) {} }
    }
    /* the control that had focus is gone from the new screen (a claimed gift, a seat just bought): the screen's heading
       takes it, never the page body, unless a sheet or an overlay holds it now */
    if (hadFocusInside && (document.activeElement === document.body || !document.activeElement) && !document.querySelector('.bg-layer,.bg-reveal,.bg-result')) {
      try { p.heading.focus({ preventScroll: true }); } catch (_) {}
    }
  }
  function rerender(view) {
    if (view) { if (panels[view] && !panels[view].section.hidden) renderPanel(view); return; }
    if (current) renderPanel(current);
  }
  function show(route) {
    const view = route.view || 'home';
    const prev = current;
    const p = panelFor(view);
    current = view;
    ctx.root.setAttribute('data-bg-view', view);
    if (prev === view) { renderPanel(view); return; }
    /* the hero page swaps the phone chrome for its dock: the chrome fades back, never snaps */
    if (prev === 'card' && view !== 'card') { animateIn(top, 'fade'); animateIn(tabs, 'fade'); }
    /* 2026-09-28: a screen left and chosen again within its leave (a quick tap away and back) was still put away when
       that leave ended: its module's hide() reset the screen on show (Party's sort and Show all, Home's chosen chapter).
       Each leave carries a token; showing the screen again, or leaving it again, makes the older leave a no-op */
    if (prev && panels[prev]) {
      const pp = panels[prev], token = pp.leaveToken = (pp.leaveToken | 0) + 1;
      pp.section.classList.add('is-leaving');
      animateOut(pp.section, 'leave').then(() => {
        if (pp.leaveToken !== token || current === prev) return;
        pp.section.classList.remove('is-leaving', 'bg-a-leave');
        pp.section.hidden = true;
        if (pp.mod && typeof pp.mod.hide === 'function') { try { pp.mod.hide(ctx, pp); } catch (_) {} }
      });
    }
    p.leaveToken = (p.leaveToken | 0) + 1;
    p.section.hidden = false;
    p.section.classList.remove('is-leaving', 'bg-a-leave');
    if (prev) { try { window.scrollTo(0, 0); } catch (_) {} }
    renderPanel(view);
    animateIn(p.section, 'enter');
  }

  /* ---------- the store drives everything ---------- */
  const unsub = store.subscribe((s, prev, changed) => {
    if (destroyed) return;
    if (changed.indexOf('route') >= 0 && (!prev.route || prev.route.path !== s.route.path || !current)) {
      ctx.lastPath = current ? (prev.route && prev.route.path) || null : null;
      paintChrome(s);
      show(s.route);
      return;
    }
    /* a request in flight changes only net.pending: that repaints the chrome, never the screen,
       so a busy button keeps its spinner and nothing under a finger is rebuilt */
    const netFlip = changed.indexOf('net') >= 0 && (!prev.net || prev.net.online !== s.net.online);
    if (netFlip || changed.some(k => k === 'home' || k === 'catalog' || k === 'cards' || k === 'route')) {
      paintChrome(s);
      if (current) renderPanel(current);
    } else if (changed.indexOf('net') >= 0) paintChrome(s);
  });
  /* the top bar gains a background once the page scrolls under it */
  let ticking = false;
  const onScroll = () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => { ticking = false; top.classList.toggle('is-stuck', window.scrollY > 4); });
  };
  window.addEventListener('scroll', onScroll, { passive: true });
  /* the phone boss square and the desktop layout follow the window */
  let lastW = window.innerWidth, lastH = window.innerHeight, rz = null;
  const onResize = () => {
    clearTimeout(rz);
    rz = setTimeout(() => {
      const w = window.innerWidth, hh = window.innerHeight;
      const crossed = (w >= 1200) !== (lastW >= 1200) || (w >= 600) !== (lastW >= 600);
      if (crossed || Math.abs(hh - lastH) > 40 || Math.abs(w - lastW) > 40) { lastW = w; lastH = hh; if (current) renderPanel(current); }
    }, 150);
  };
  window.addEventListener('resize', onResize);
  paintChrome(store.get());

  return {
    el: el,
    attachRouter(r) { router = r; },
    rerender: rerender,
    bump: bump,
    pill: pillOf,
    countTo: countTo,
    panel: v => panels[v] || null,
    destroy() {
      destroyed = true;
      clearInterval(timerTick);
      unsub();
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
      el.remove();
    }
  };
}

/* ---------- the chrome's own styles (2026-09-28), once, at the end of body so they win ties with the section ----------
   - the wallet: one capsule, three segments with hairline dividers; a changed value glints (a light sweeps across it)
     and its number flashes; the refill timer hangs under the ticket segment on a phone and sits inline on a desktop
   - your Champion: a chip from tablet width up (face, the name in the display serif, the stars in gold) that lifts on
     hover and presses in; a phone keeps the face alone
   - the brand's diamond turns once, catching the light, as the page arrives
   - the desktop nav: the tabs at the top, Settings (and the admin's page) at the foot of the column under a fading rule */
function shellCss() {
  if (document.getElementById('bg-shell-css')) return;
  const u = n => 'calc(' + n + '*var(--u))';
  document.body.appendChild(h('style', { id: 'bg-shell-css', text:
    '.bg-top .bg-wallet{display:inline-flex;align-items:center;height:' + u(36) + ';padding:0 ' + u(2) + ';border-radius:' + u(12) + ';background:linear-gradient(180deg,rgba(28,30,38,.94),rgba(15,17,22,.94));box-shadow:inset 0 1px 0 rgba(255,255,255,.07),0 0 0 1px var(--c-line),0 ' + u(8) + ' ' + u(20) + ' ' + u(-12) + ' rgba(0,0,0,.9)}'
    + '.bg-top .bg-wallet .bg-pill{position:relative;height:' + u(32) + ';padding:0 ' + u(10) + ';gap:' + u(6) + ';background:none;box-shadow:none;border-radius:' + u(10) + '}'
    + '.bg-top .bg-wallet>*+*::before{content:"";position:absolute;left:0;top:' + u(9) + ';bottom:' + u(9) + ';width:1px;background:var(--c-line)}'
    + '.bg-top .bg-tixseg{position:relative;display:inline-flex;align-items:center;height:100%}'
    + '.bg-top .bg-wallet .bg-pill::after{content:"";position:absolute;inset:0;border-radius:inherit;pointer-events:none;opacity:0;background:linear-gradient(105deg,rgba(255,255,255,0) 35%,rgba(255,236,190,.30) 50%,rgba(255,255,255,0) 65%) no-repeat;background-size:260% 100%}'
    + '.bg-top .bg-wallet .bg-pill.is-glint::after{animation:bgShellGlint 760ms var(--ease)}'
    + '@keyframes bgShellGlint{0%{opacity:1;background-position:130% 0}75%{opacity:1}100%{opacity:0;background-position:-30% 0}}'
    + '.bg-top .bg-wallet .bg-pill.is-up .bg-pill__v{animation:bgShellUp 820ms var(--ease)}'
    + '.bg-top .bg-wallet .bg-pill.is-down .bg-pill__v{animation:bgShellDown 820ms var(--ease)}'
    + '@keyframes bgShellUp{0%,100%{color:var(--c-ink);text-shadow:none}25%{color:#FFE3A1;text-shadow:0 0 ' + u(10) + ' rgba(255,210,122,.75)}}'
    + '@keyframes bgShellDown{0%,100%{color:var(--c-ink)}25%{color:var(--c-goldhi)}}'
    + '.bg-top .bg-wallet .bg-pill.bg-bump{animation:bgShellBump 420ms var(--ease)}'
    + '@keyframes bgShellBump{0%,100%{transform:scale(1)}35%{transform:scale(1.07)}}'
    + '.bg-top .bg-pill__timer{display:inline-flex;align-items:center;gap:' + u(3) + ';font:600 ' + u(11) + '/' + u(14) + ' var(--f-ui);font-variant-numeric:tabular-nums;white-space:nowrap;color:var(--c-goldhi)}'
    + '.bg-top .bg-pill__timer[hidden]{display:none}'
    + '@media (max-width:1199px){.bg-top .bg-pill__timer{position:absolute;left:50%;top:100%;transform:translate(-50%,' + u(-5) + ');height:' + u(16) + ';padding:0 ' + u(6) + ';border-radius:999px;font-size:' + u(11) + ';background:#1B1813;box-shadow:0 0 0 1px rgba(217,178,106,.42),0 ' + u(2) + ' ' + u(6) + ' rgba(0,0,0,.6)}}'
    + '@media (min-width:1200px){.bg-top .bg-pill__timer{height:' + u(16) + ';margin:0 ' + u(12) + ' 0 ' + u(-4) + ';padding-left:' + u(8) + ';border-left:1px solid var(--c-line);color:var(--c-muted)}'
    + '.bg-top .bg-wallet{height:' + u(40) + '}.bg-top .bg-wallet .bg-pill{height:' + u(36) + ';padding:0 ' + u(12) + '}}'
    + '@media (min-width:600px){.bg-top .bg-prof{height:' + u(48) + ';padding:' + u(4) + ' ' + u(14) + ' ' + u(4) + ' ' + u(6) + ';gap:' + u(10) + ';border-radius:' + u(14) + ';background:rgba(18,20,26,.6);box-shadow:inset 0 1px 0 var(--c-edge),0 0 0 1px var(--c-line);transition:transform 180ms var(--ease),box-shadow 180ms var(--ease),background-color 180ms var(--ease)}'
    + '.bg-top .bg-prof__k{font-family:var(--f-disp);font-size:' + u(17) + ';line-height:' + u(20) + ';font-weight:500;color:var(--c-ink)}'
    + '.bg-top .bg-prof__v{display:inline-flex;align-items:center;gap:' + u(4) + ';font-size:' + u(12) + ';line-height:' + u(16) + ';font-weight:600;color:var(--c-goldhi)}}'
    + '@media (hover:hover) and (min-width:600px){.bg-top .bg-prof:hover{transform:translateY(' + u(-1) + ');background:rgba(24,26,34,.85);box-shadow:inset 0 1px 0 var(--c-edge),0 0 0 1px rgba(217,178,106,.42),0 ' + u(8) + ' ' + u(18) + ' ' + u(-10) + ' rgba(217,178,106,.4)}}'
    + '.bg-top .bg-prof:active{transform:scale(.96)}'
    + '.bg-brand__mark{display:inline-grid;place-items:center}'
    + '.bg-brand.is-glint .bg-brand__mark svg{animation:bgShellMark 1100ms var(--ease) 250ms both}'
    + '@keyframes bgShellMark{0%{transform:rotate(0) scale(1);filter:none}45%{transform:rotate(90deg) scale(1.18);filter:drop-shadow(0 0 ' + u(8) + ' rgba(255,214,130,.85))}100%{transform:rotate(90deg) scale(1);filter:none}}'
    + '@media (hover:hover){.bg-brand__mark svg{transition:filter 240ms var(--ease)}.bg-brand:hover .bg-brand__mark svg{filter:drop-shadow(0 0 ' + u(6) + ' rgba(255,214,130,.7))}}'
    + '@media (min-width:1200px){.bg-shell:not(.has-banner) .bg-tabs{min-height:calc(100svh - ' + u(112) + ')}'
    + '.bg-dnav{margin-top:auto;padding-bottom:' + u(4) + '}'
    + '.bg-dnav::before{margin:0 ' + u(12) + ' ' + u(12) + ';background:linear-gradient(90deg,rgba(201,208,218,0),var(--c-line) 25%,var(--c-line) 75%,rgba(201,208,218,0))}'
    + '.bg-dnav__item{transition:background-color 180ms var(--ease),color 180ms var(--ease)}'
    + '.bg-dnav__item:hover{background:var(--c-surface)}'
    + '.bg-dnav__item .bg-ico{transition:transform 260ms var(--ease)}.bg-dnav__item:hover .bg-ico{transform:translateX(' + u(2) + ')}}'
    + '@media (prefers-reduced-motion:reduce){.bg-top .bg-wallet .bg-pill::after,.bg-brand.is-glint .bg-brand__mark svg,.bg-top .bg-wallet .bg-pill .bg-pill__v{animation:none!important}}'
    + 'html.bg-rm .bg-top .bg-wallet .bg-pill::after,html.bg-rm .bg-brand.is-glint .bg-brand__mark svg,html.bg-rm .bg-top .bg-wallet .bg-pill .bg-pill__v{animation:none!important}' }));
}

/* ---------- Settings (§10): Endless speed, the intro, Auto-cast, motion, the Codex, history ----------
   Auto-cast writes the fight's own choice (hoh-autocast-v2, as its round menu does: 1 on, 0 off,
   unset follows the device), so the two never disagree. Motion is this page's own: reduce it here and menus and
   cutscenes change with a fade only, whatever the device says. The game has no sound yet, so there
   is no sound row. */
const AUTOCAST_KEY = 'hoh-autocast-v2';
function readAutocast() { try { const v = localStorage.getItem(AUTOCAST_KEY); return v === '1' ? 'on' : v === '0' ? 'off' : 'device'; } catch (_) { return 'device'; } }
const SPEED16_KEY = 'hoh-battle-speed16-v1';
function readSpeed16() { try { return localStorage.getItem(SPEED16_KEY) === '1' ? 'on' : 'off'; } catch (_) { return 'off'; } }
/* 2026-09-26 (Jan): "remove the 2x,4x,8x,16x speed for now, only give it to my account". The fight gates the
   speed the same way (hoh-contract-ui.js speedOwner). */
function speedOwner() {
  try { const t = JSON.parse(localStorage.getItem('hoh-your-hall-token') || 'null'); return String((t && t.email) || '').trim().toLowerCase() === 'fondlyframed@gmail.com'; } catch (_) { return false; }
}
function writeSpeed16(v) { try { if (v === 'on') localStorage.setItem(SPEED16_KEY, '1'); else localStorage.removeItem(SPEED16_KEY); } catch (_) {} }
function writeAutocast(v) { try { if (v === 'on') localStorage.setItem(AUTOCAST_KEY, '1'); else if (v === 'off') localStorage.setItem(AUTOCAST_KEY, '0'); else localStorage.removeItem(AUTOCAST_KEY); } catch (_) {} }
/* Endless speed (the menus redesign, 2026-10-05): the speed an Endless run starts at. Speed is presentation only (the
   tape, the rewards and the server never see it), so it is this device's choice. ENDLESS_SPEED_KEY holds 1, 2, 4, 8 or
   16; the fight starts a run there, or at the fastest speed it has open when that is lower. The fight opens speeds by
   the best Endless round (home.unlimited.best_round): 2x at Best 10 and 4x at Best 25 for everyone, 8x at Best 35 and
   16x at Best 45 for the owner's account alone (hoh-contract-ui.js unlimSpeedMax), so a player is offered 1x, 2x and
   4x and the owner all five. 16x stays the fight's own opt-in (SPEED16_KEY, R763): choosing 16x here sets it, any
   other choice clears it. With nothing chosen the row shows what the fight does today, the fastest open speed (8x at
   most unless 16x was chosen). */
const ENDLESS_SPEED_KEY = 'hoh-endless-speed-v1';
const SPEEDS = Object.freeze([[1, 0], [2, 10], [4, 25], [8, 35], [16, 45]]);
function speedChoices(ctx) {
  const best = ((ctx.home().unlimited || {}).best_round) | 0, own = speedOwner();
  return SPEEDS.filter(s => own || s[0] <= 4).map(s => ({ x: s[0], at: s[1], open: best >= s[1] }));
}
function readEndlessSpeed(ctx) {
  const list = speedChoices(ctx), top = list.filter(c => c.open).pop().x;
  let v = 0;
  try { v = parseInt(localStorage.getItem(ENDLESS_SPEED_KEY), 10) | 0; } catch (_) { v = 0; }
  if (!SPEEDS.some(s => s[0] === v)) v = readSpeed16() === 'on' ? 16 : 8;
  return Math.min(v, top);
}
function writeEndlessSpeed(v) {
  const x = parseInt(v, 10) | 0;
  try { localStorage.setItem(ENDLESS_SPEED_KEY, String(x)); } catch (_) {}
  writeSpeed16(x === 16 ? 'on' : 'off');
}
export function settingsSheet(ctx) {
  const choose = (label, id, value, options, onChange, sub) => {
    const sel = h('select', { class: 'bg-select', id: id, 'data-fk': id }, options.map(o => h('option', { value: o[0], text: o[1], disabled: o[2] ? true : null })));
    sel.value = value;
    sel.addEventListener('change', () => onChange(sel.value));
    if (sub) return h('div', { class: 'bg-set bg-set--sub' }, h('span', { class: 'bg-set__txt' }, h('label', { for: id, text: label }), h('span', { text: sub })), sel);
    return h('div', { class: 'bg-set' }, h('label', { class: 'bg-set__t', for: id, text: label }), sel);
  };
  const row = (ico, label, sub, act, fk) => h('button', { type: 'button', class: 'bg-set bg-set--go', 'data-fk': fk, on: { click: act } },
    h('span', { class: 'bg-set__ico', 'aria-hidden': 'true' }, icon(ico, 20)), h('span', { class: 'bg-set__txt' }, h('b', { text: label }), sub ? h('span', { text: sub }) : null), icon('chevR', 18));
  let s = null;
  const after = fn => () => { const p = s ? s.close() : Promise.resolve(); p.then(fn); };
  /* a speed not open yet says the best round that opens it, and cannot be chosen */
  const speeds = speedChoices(ctx).map(c => [String(c.x), c.x + 'x' + (c.open ? '' : ', at Best ' + c.at), !c.open]);
  const body = [
    /* (the hook keeps the old 16x row's name, set-speed: this row took its place) */
    choose('Endless speed', 'set-speed', String(readEndlessSpeed(ctx)), speeds, writeEndlessSpeed, 'Where every Endless run starts'),
    choose('Auto-cast cards', 'set-autocast', readAutocast(), [['device', 'Match my device'], ['on', 'Always'], ['off', 'Never']], writeAutocast),
    choose('Motion', 'set-motion', motionPref(), [['device', 'Match my device'], ['reduce', 'Reduce motion']], v => setMotionPref(v)),
    row('codex', 'Codex', 'Every effect and term', after(() => ctx.codex()), 'set-codex'),
    row('film', 'Watch the intro', 'The Hush, about 45 seconds', after(() => ctx.cutscene.intro()), 'set-intro'),
    row('history', 'History', 'Gold, crystals, tickets and packs', after(() => ctx.history()), 'set-history')
  ];
  if (ctx.home().admin === true) {
    body.push(row('pencil', 'Game rarity and boss posters', null, after(() => ctx.go('#/admin')), 'set-admin'));
    body.push(row('film', 'Preview this chapter\u2019s boss card', null, after(() => ctx.cutscene.bossCard(ctx.bossDef(ctx.currentLevel()) && ctx.bossDef(ctx.currentLevel()).id)), 'set-bosscard'));
    body.push(row('film', 'Preview Oran\u2019s second face', null, after(() => ctx.cutscene.oranPhase()), 'set-oran'));
  }
  body.push(h('a', { class: 'bg-set bg-set--go', href: ctx.hallUrl, 'data-bg-action': 'hall' }, h('span', { class: 'bg-set__ico', 'aria-hidden': 'true' }, icon('hall', 20)),
    h('span', { class: 'bg-set__txt' }, h('b', { text: 'Back to the Hall' })), icon('chevR', 18)));
  s = sheet({ title: 'Settings', name: 'settings', body: h('div', { class: 'bg-sets' }, body.filter(Boolean)) });
  return s;
}
